/** Whether a chosen booking day sits outside the studded-tire window. */

type SpikedTireDateSide = 'before' | 'after';

export type SpikedTireBookingSeason = {
  /** Chosen day, `yyyy-mm-dd`. Null when the customer has not picked a date. */
  bookingDate: string | null;
  /** Day the season window was computed for, `yyyy-mm-dd`. */
  asOf: string;
  seasonStart: string | null;
  seasonEnd: string | null;
  isInSeason: boolean;
  /**
   * End of the season that already finished, when `asOf` is after it.
   * The season endpoint then returns next fall, so this comes from a second lookup.
   */
  endedSeasonEnd: string | null;
};

export type SpikedTireBookingSeasonDecision = {
  outside: SpikedTireDateSide | null;
  /** Second lookup day, when a spring date is after the season that just ended. */
  endedSeasonAsOf: string | null;
  /** In season only because an early exception covers the day, before the legal start. */
  early: boolean;
};

const springHalf = (apiDay: string): boolean => Number(apiDay.slice(5, 7)) <= 6;

/**
 * Fall dates before the season start are too early. Spring dates after the
 * season that just ended are too late. A date inside the window, including an
 * early-season exception, is allowed.
 */
export const spikedTireBookingSeason = ({
  bookingDate,
  asOf,
  seasonStart,
  seasonEnd,
  isInSeason,
  endedSeasonEnd
}: SpikedTireBookingSeason): SpikedTireBookingSeasonDecision => {
  if (seasonStart == null || seasonEnd == null) {
    return { outside: null, endedSeasonAsOf: null, early: false };
  }

  const day = bookingDate ?? asOf;
  if (isInSeason) {
    return { outside: null, endedSeasonAsOf: null, early: day < seasonStart };
  }
  if (bookingDate == null || bookingDate >= seasonStart) {
    return { outside: null, endedSeasonAsOf: null, early: false };
  }
  if (!springHalf(bookingDate)) {
    return { outside: 'before', endedSeasonAsOf: null, early: false };
  }
  if (endedSeasonEnd == null) {
    return { outside: null, endedSeasonAsOf: `${bookingDate.slice(0, 4)}-01-01`, early: false };
  }
  if (bookingDate > endedSeasonEnd) {
    return { outside: 'after', endedSeasonAsOf: null, early: false };
  }
  return { outside: null, endedSeasonAsOf: null, early: false };
};
